import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Bot,
  Users,
  DollarSign,
  Activity,
  TrendingUp,
  Settings,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Eye,
  Star,
  Crown
} from 'lucide-react';
import { PremiumBot } from '@/types/premium-bots';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { usePremiumBotDetailAdmin } from '@/hooks/use-premium-bot-detail-admin';
import { usePropBotOperations } from '@/hooks/use-prop-bot-operations';
import { AccountOperationsToolbar } from '../prop-trading/AccountOperationsToolbar';
import { EnhancedAccountCard } from '../prop-trading/EnhancedAccountCard';
import { AnalyticsExportTools } from '../prop-trading/analytics/AnalyticsExportTools';
import { CapitalDistributionChart } from '../prop-trading/analytics/CapitalDistributionChart';

interface PremiumBotDetailModalProps {
  bot: PremiumBot | null;
  isOpen: boolean;
  onClose: () => void;
}

export const PremiumBotDetailModal: React.FC<PremiumBotDetailModalProps> = ({
  bot,
  isOpen,
  onClose,
}) => {
  const [selectedAccounts, setSelectedAccounts] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [accountTypeFilter, setAccountTypeFilter] = useState('all');

  const {
    connectedAccounts,
    accountsStats,
    isLoading,
    refetch,
    forceRefresh,
    syncBalances,
    isSyncingBalances,
    syncResult,
    clearCache,
    isClearingCache,
    cacheStats
  } = usePremiumBotDetailAdmin(bot?.id);

  const {
    enableAccount,
    disableAccount,
    disconnectAccount,
    performBulkEnable,
    performBulkDisable,
    performBulkDisconnect,
    exportAccounts,
    isProcessing
  } = usePropBotOperations(bot?.id);

  const handleSyncBalances = async () => {
    if (!bot?.id) return;
    
    try {
      const result = await syncBalances(bot.id);
      if (result.success) {
        toast.success(`Balance sync completed: ${result.synced} accounts synced, ${result.failed} failed`);
      } else {
        toast.error(`Balance sync failed: ${result.errors.join(', ')}`);
      }
    } catch (error) {
      toast.error('Failed to sync balances');
    }
  };

  const handleForceRefresh = async () => {
    try {
      await forceRefresh();
      toast.success('Data refreshed successfully');
    } catch (error) {
      toast.error('Failed to refresh data');
    }
  };

  const handleClearCache = async () => {
    try {
      await clearCache();
      toast.success('Cache cleared successfully');
    } catch (error) {
      toast.error('Failed to clear cache');
    }
  };

  // Filter and search logic
  const filteredAccounts = useMemo(() => {
    return connectedAccounts.filter(account => {
      // Search filter
      const matchesSearch = searchTerm === '' || 
        account.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        account.userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
        account.accountNumber.toLowerCase().includes(searchTerm.toLowerCase());

      // Status filter
      const matchesStatus = statusFilter === 'all' || account.status === statusFilter;

      // Account type filter
      let matchesType = true;
      if (accountTypeFilter === 'live') matchesType = account.isLive;
      else if (accountTypeFilter === 'demo') matchesType = !account.isLive;
      else if (accountTypeFilter === 'reverse') matchesType = account.isReversePosition;
      else if (accountTypeFilter === 'normal') matchesType = !account.isReversePosition;

      return matchesSearch && matchesStatus && matchesType;
    });
  }, [connectedAccounts, searchTerm, statusFilter, accountTypeFilter]);

  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedAccounts(new Set(filteredAccounts.map(account => account.id)));
    } else {
      setSelectedAccounts(new Set());
    }
  };

  const handleAccountSelection = (accountId: string, selected: boolean) => {
    const newSelection = new Set(selectedAccounts);
    if (selected) {
      newSelection.add(accountId);
    } else {
      newSelection.delete(accountId);
    }
    setSelectedAccounts(newSelection);
  };

  // Account operations
  const handleSingleAccountEnable = async (accountId: string) => {
    try {
      await enableAccount(accountId);
    } catch (error) {
      // Error handled by hook
    }
  };

  const handleSingleAccountDisable = async (accountId: string) => {
    try {
      await disableAccount(accountId);
    } catch (error) {
      // Error handled by hook
    }
  };

  const handleSingleAccountDisconnect = async (accountId: string) => {
    try {
      await disconnectAccount(accountId);
      // Remove from selection if it was selected
      const newSelection = new Set(selectedAccounts);
      newSelection.delete(accountId);
      setSelectedAccounts(newSelection);
    } catch (error) {
      // Error handled by hook
    }
  };

  // Bulk operations
  const handleBulkEnable = async () => {
    try {
      await performBulkEnable(Array.from(selectedAccounts));
      setSelectedAccounts(new Set());
    } catch (error) {
      // Error handled by hook
    }
  };

  const handleBulkDisable = async () => {
    try {
      await performBulkDisable(Array.from(selectedAccounts));
      setSelectedAccounts(new Set());
    } catch (error) {
      // Error handled by hook
    }
  };

  const handleBulkDisconnect = async () => {
    try {
      await performBulkDisconnect(Array.from(selectedAccounts));
      setSelectedAccounts(new Set());
    } catch (error) {
      // Error handled by hook
    }
  };

  const handleExport = () => {
    exportAccounts(filteredAccounts);
  };

  // Analytics data for premium bots
  const analyticsData = useMemo(() => {
    const capitalDistribution = [
      { name: 'Live Accounts', value: accountsStats.liveAccounts, count: accountsStats.liveAccounts },
      { name: 'Demo Accounts', value: accountsStats.demoAccounts, count: accountsStats.demoAccounts }
    ];

    const positionTypeDistribution = [
      { name: 'Normal', value: filteredAccounts.filter(acc => !acc.isReversePosition).length },
      { name: 'Reverse', value: accountsStats.reversePositions }
    ];

    const brokerDistribution = filteredAccounts.reduce((acc, account) => {
      const broker = account.broker || 'Unknown';
      const existing = acc.find(item => item.name === broker);
      if (existing) {
        existing.value++;
      } else {
        acc.push({ name: broker, value: 1 });
      }
      return acc;
    }, [] as { name: string; value: number }[]);

    return {
      totalCapital: accountsStats.totalCapital,
      averageBalance: accountsStats.averageBalance,
      capitalDistribution,
      positionTypeDistribution,
      brokerDistribution,
      topUsers: connectedAccounts
        .reduce((acc, account) => {
          const existing = acc.find(u => u.userId === account.userId);
          if (existing) {
            existing.totalCapital += account.balance || 0;
            existing.accountCount++;
          } else {
            acc.push({
              userId: account.userId,
              userName: account.userName,
              userEmail: account.userEmail,
              totalCapital: account.balance || 0,
              accountCount: 1,
              lastActivity: account.addedDate
            });
          }
          return acc;
        }, [] as any[])
        .sort((a, b) => b.totalCapital - a.totalCapital)
        .slice(0, 10)
    };
  }, [connectedAccounts, filteredAccounts, accountsStats]);

  if (!bot) return null;

  const getBotStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return (
          <Badge className="bg-green-100 text-green-800 border-green-200">
            <CheckCircle className="h-3 w-3 mr-1" />
            Active
          </Badge>
        );
      case 'inactive':
        return (
          <Badge variant="secondary">
            <AlertCircle className="h-3 w-3 mr-1" />
            Inactive
          </Badge>
        );
      default:
        return (
          <Badge variant="outline">
            {status}
          </Badge>
        );
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="!w-[95vw] lg:!w-[80vw] !max-w-none max-h-[95vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Crown className="h-6 w-6 text-amber-500" />
            <div>
              <div className="font-semibold">{bot.name}</div>
              <div className="text-sm text-muted-foreground font-normal">
                Premium Bot Management
              </div>
            </div>
            {getBotStatusBadge(bot.status)}
          </DialogTitle>
        </DialogHeader>

        <div className="custom-scrollbar scrollbar-thin scrollbar-thumb-border/40 scrollbar-track-transparent hover:scrollbar-thumb-border/60 overflow-y-auto max-h-[75vh] space-y-6">
          {/* Quick Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Connected Accounts</p>
                    <p className="text-2xl font-bold">
                      {isLoading ? <Skeleton className="h-8 w-12" /> : accountsStats?.totalAccounts || 0}
                    </p>
                  </div>
                  <Users className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Subscribers</p>
                    <p className="text-2xl font-bold">
                      {isLoading ? <Skeleton className="h-8 w-12" /> : accountsStats?.totalSubscribers || 0}
                    </p>
                  </div>
                  <Star className="h-8 w-8 text-amber-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Total Capital</p>
                    <p className="text-2xl font-bold">
                      {isLoading ? (
                        <Skeleton className="h-8 w-20" />
                      ) : (
                        `$${(accountsStats?.totalCapital || 0).toLocaleString()}`
                      )}
                    </p>
                  </div>
                  <DollarSign className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Avg Balance</p>
                    <p className="text-2xl font-bold">
                      {isLoading ? <Skeleton className="h-8 w-12" /> : `$${(accountsStats?.averageBalance || 0).toLocaleString()}`}
                    </p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Bot Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Premium Bot Information & Management
                </span>
                <div className="flex gap-2">
                  <AnalyticsExportTools
                    botId={bot.id}
                    botName={bot.name}
                    analytics={analyticsData}
                    connectedAccounts={connectedAccounts}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleForceRefresh}
                    disabled={isLoading}
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSyncBalances}
                    disabled={isSyncingBalances}
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${isSyncingBalances ? 'animate-spin' : ''}`} />
                    Sync Balances
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Bot ID</span>
                    <span className="text-sm font-mono">{bot.id}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Type</span>
                    <Badge variant="outline" className="text-amber-600 border-amber-200">
                      <Crown className="h-3 w-3 mr-1" />
                      Premium Bot
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Created</span>
                    <span className="text-sm">
                      {formatDistanceToNow(new Date(bot.created_date), { addSuffix: true })}
                    </span>
                  </div>
                </div>
                
                {bot.premiumDetails && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Trading Style</span>
                      <Badge variant="secondary">{bot.premiumDetails.trading_style}</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Profit Share</span>
                      <span className="text-sm font-medium text-green-600">
                        {bot.premiumDetails.profit_share}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Min Capital</span>
                      <span className="text-sm font-medium">
                        ${Number(bot.premiumDetails.min_capital || 0).toLocaleString()}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Analytics Dashboard */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <CapitalDistributionChart
              data={analyticsData.capitalDistribution}
              isLoading={isLoading}
              title="Account Distribution"
              showValue={false}
            />
            <CapitalDistributionChart
              data={analyticsData.brokerDistribution}
              isLoading={isLoading}
              title="Broker Distribution"
              showValue={false}
            />
          </div>

          {/* Connected Accounts with Operations */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Connected Trading Accounts
                </span>
                {filteredAccounts.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={selectedAccounts.size === filteredAccounts.length && filteredAccounts.length > 0}
                      onCheckedChange={handleSelectAll}
                      disabled={isProcessing}
                    />
                    <span className="text-sm text-muted-foreground">Select All</span>
                  </div>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-4">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Skeleton className="h-10 w-10 rounded-full" />
                          <div>
                            <Skeleton className="h-4 w-32 mb-2" />
                            <Skeleton className="h-3 w-48" />
                          </div>
                        </div>
                        <div className="text-right">
                          <Skeleton className="h-6 w-20 mb-2" />
                          <Skeleton className="h-4 w-16" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : connectedAccounts.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Eye className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                  <p>No connected accounts found</p>
                  <p className="text-sm mt-2">This premium bot has not been subscribed by any users yet.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Operations Toolbar */}
                  <AccountOperationsToolbar
                    accounts={connectedAccounts}
                    selectedAccounts={selectedAccounts}
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                    statusFilter={statusFilter}
                    onStatusFilterChange={setStatusFilter}
                    accountTypeFilter={accountTypeFilter}
                    onAccountTypeFilterChange={setAccountTypeFilter}
                    onBulkEnable={handleBulkEnable}
                    onBulkDisable={handleBulkDisable}
                    onBulkDisconnect={handleBulkDisconnect}
                    onExport={handleExport}
                    isProcessing={isProcessing}
                  />

                  {/* Account Cards */}
                  {filteredAccounts.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Eye className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                      <p>No accounts match your filters</p>
                      <p className="text-sm mt-2">Try adjusting your search or filter criteria.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-4">
                      {filteredAccounts.map((account) => (
                        <EnhancedAccountCard
                          key={account.id}
                          account={account}
                          isSelected={selectedAccounts.has(account.id)}
                          onSelectionChange={(selected) => handleAccountSelection(account.id, selected)}
                          onEnable={() => handleSingleAccountEnable(account.id)}
                          onDisable={() => handleSingleAccountDisable(account.id)}
                          onDisconnect={() => handleSingleAccountDisconnect(account.id)}
                          onRefreshBalance={() => handleSyncBalances()}
                          isProcessing={isProcessing}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};